import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  "https://ai-workshop-llc-backend.onrender.com";

// ─── Skeleton block ───────────────────────────────────────────────────────────
function Skeleton({ className = "" }) {
  return (
    <div
      className={`animate-pulse rounded-xl bg-white/5 ${className}`}
    />
  );
}

// ─── Card component ───────────────────────────────────────────────────────────
function PlanCard({ label, body, accent }) {
  const accents = {
    emerald: {
      border: "border-blue-500/25",
      bg:     "bg-blue-500/5",
      dot:    "bg-blue-400",
      label:  "text-blue-400",
    },
    blue: {
      border: "border-blue-500/25",
      bg:     "bg-blue-500/5",
      dot:    "bg-blue-400",
      label:  "text-blue-400",
    },
    purple: {
      border: "border-purple-500/25",
      bg:     "bg-purple-500/5",
      dot:    "bg-purple-400",
      label:  "text-purple-400",
    },
  };
  const c = accents[accent] ?? accents.emerald;

  return (
    <div
      className={`rounded-2xl border ${c.border} ${c.bg} backdrop-blur-xl p-6 flex flex-col gap-3`}
    >
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full shrink-0 ${c.dot}`} />
        <span className={`text-[11px] font-bold uppercase tracking-widest ${c.label}`}>
          {label}
        </span>
      </div>
      <p className="text-slate-200 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function LeadMagnetPage() {
  const { leadId } = useParams();
  const navigate   = useNavigate();

  const [plan,    setPlan]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    document.title = "Your AI Implementation Plan — AI Workshop";
  }, []);

  useEffect(() => {
    if (!leadId) { setLoading(false); setExpired(true); return; }
    fetch(`${API_BASE}/api/lead/${leadId}`)
      .then(r => {
        if (r.status === 404) { setExpired(true); return null; }
        if (!r.ok) throw new Error("fetch failed");
        return r.json();
      })
      .then(d => { if (d) setPlan(d); })
      .catch(() => setExpired(true))
      .finally(() => setLoading(false));
  }, [leadId]);

  const auditHref = plan?.vertical
    ? `/audit?industry=${encodeURIComponent(plan.vertical)}`
    : "/audit";

  // ── Loading skeleton ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
        style={{ background: "#0f172a" }}
      >
        <div className="w-full max-w-3xl space-y-6">
          <Skeleton className="h-10 w-3/4 mx-auto" />
          <Skeleton className="h-4 w-1/2 mx-auto" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
          </div>
          <Skeleton className="h-24 mt-6" />
        </div>
      </div>
    );
  }

  // ── 404 / expired ───────────────────────────────────────────────────────────
  if (expired || !plan) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 py-16 text-center"
        style={{ background: "#0f172a" }}
      >
        <p className="text-slate-400 text-sm mb-2 uppercase tracking-widest font-semibold">
          Plan Expired
        </p>
        <h1
          className="text-3xl font-bold text-white mb-4"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          This plan has expired.
        </h1>
        <p className="text-slate-400 text-base mb-8 max-w-md">
          Generate a fresh one — takes 60 seconds and it's free.
        </p>
        <a
          href="/audit"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm
                     bg-[#3b82f6] text-[#0f172a] hover:bg-[#3cc990] transition-colors"
        >
          Generate a fresh plan →
        </a>
      </div>
    );
  }

  // ── Success ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen flex flex-col items-center px-6 py-16"
      style={{ background: "#0f172a" }}
    >
      <div className="w-full max-w-3xl">

        {/* Badge */}
        <div className="flex justify-center mb-6">
          <span className="text-[11px] font-bold uppercase tracking-widest text-[#3b82f6] bg-[#3b82f6]/10 border border-[#3b82f6]/20 rounded-full px-4 py-1.5">
            Your AI Implementation Plan
          </span>
        </div>

        {/* Headline */}
        <h1
          className="text-3xl sm:text-4xl font-bold text-white text-center leading-tight mb-10"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {plan.headline}
        </h1>

        {/* Three cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <PlanCard
            label="Immediate Win"
            body={plan.immediate_win}
            accent="emerald"
          />
          <PlanCard
            label="30-Day Goal"
            body={plan.thirty_day_goal}
            accent="blue"
          />
          <PlanCard
            label="90-Day Vision"
            body={plan.vision}
            accent="purple"
          />
        </div>

        {/* CTA section */}
        <div className="rounded-2xl border border-white/8 bg-white/3 backdrop-blur-xl px-8 py-8 text-center">
          <p
            className="text-white text-lg font-semibold mb-2"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Want your complete 90-day AI implementation blueprint?
          </p>
          <p className="text-slate-400 text-sm mb-6 max-w-md mx-auto">
            A full audit maps every tool, timeline, and ROI projection for your
            specific business — personalized in under 2 minutes.
          </p>
          <a
            href={auditHref}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm
                       bg-[#3b82f6] text-[#0f172a] hover:bg-[#3cc990] transition-colors"
          >
            Start Your Free Audit →
          </a>
          <p className="text-slate-600 text-xs mt-4">No credit card required</p>
        </div>

      </div>
    </div>
  );
}
